import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import dayjs from 'dayjs';
import {
  Spinner,
  Alert,
  Card,
  Row,
  Col,
  Badge,
  Button,
} from 'react-bootstrap';

import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

// Lucide React Icons
import {
  ArrowLeft,
  FileText,
  Calendar,
  DollarSign,
  Building,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  Edit,
  Archive,
  CreditCard,
  Receipt,
  FileImage,
  File,
  User,
  Hash,
  CalendarDays,
  Banknote,
  Timer,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';

// Import your API utility function
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageTitle from '../../../layouts/PageTitle';

// Import custom components
import ConfirmationModal from './ConfirmationModal';
import VerificationModal from './VerificationModal';
import VerificationDetailsModal from '../../Accounting/Verification/VerificationDetailsModal';

const ViewSupplierInvoice = () => {
  const { companyId, invoiceId } = useParams();
  const history = useHistory();

  // State variables
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [attachmentUrls, setAttachmentUrls] = useState({});
  const [error, setError] = useState(null);
  const [markAsPaidLoading, setMarkAsPaidLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);

  // New state for payment date
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [verifications, setVerifications] = useState([]);

  // Fetch the invoice details
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const getSupplierInvoiceApiUrl = getApiUrl(
          `${companyId}/purchasing/supplier-invoices/${invoiceId}`
        );
        const response = await axios.get(getSupplierInvoiceApiUrl, {
          withCredentials: true,
        });
        console.log('Invoice details:', response.data);
        setInvoice(response.data);
      } catch (error) {
        console.error('Error fetching invoice details:', error);
        setError('Kunde inte hämta fakturadetaljer. Vänligen försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    const fetchAssociatedVerifications = async () => {
      try {
        const getAssociatedVerificationsApiUrl = getApiUrl(
          `${companyId}/purchasing/supplier-invoices/${invoiceId}/verifications`
        );

        const response = await axios.get(getAssociatedVerificationsApiUrl, {
          withCredentials: true,
        });

        console.log('Associated verifications:', response.data);
        setVerifications(response.data);
      } catch (error) {
        console.error('Error fetching associated verifications:', error);
        setError('Kunde inte hämta associerade verifikationer. Vänligen försök igen senare.');
      }
    };

    fetchInvoice();
    fetchAssociatedVerifications();
  }, [companyId, invoiceId]);

  // Fetch attachments when the invoice changes
  useEffect(() => {
    const fetchAttachments = async () => {
      if (invoice && invoice.attachments && invoice.attachments.length > 0 && invoice.attachments[0].attachment_id) {
        const urls = {};
        for (const attachment of invoice.attachments) {
          try {
            const attachmentUrl = await fetchAttachment(attachment.attachment_id);
            urls[attachment.attachment_id] = attachmentUrl;
          } catch (error) {
            console.error('Error fetching attachment:', error);
            setError('Ett fel uppstod vid hämtning av bilagor.');
          }
        }
        setAttachmentUrls(urls);
      }
    };

    fetchAttachments();
  }, [invoice]);

  // Function to fetch an attachment from the backend and create a Blob URL
  const fetchAttachment = async (attachmentId) => {
    const attachmentApiUrl = getApiUrl(`${companyId}/attachments/${attachmentId}`);
    const response = await axios.get(attachmentApiUrl, {
      withCredentials: true,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });
    const url = URL.createObjectURL(blob);
    return url;
  };

  // Cleanup Blob URLs when component unmounts or attachmentUrls change
  useEffect(() => {
    return () => {
      // Revoke Blob URLs to free memory
      Object.values(attachmentUrls).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [attachmentUrls]);

  const getStatusDisplayName = (status) => {
    const statusNames = {
      'Scanned': 'Faktura inläst',
      'Pending Approval': 'Väntar på godkännande',
      'Approved': 'Godkänd',
      'Pending Payment': 'Väntar på betalning',
      'Paid': 'Betald',
      'Verification Created': 'Bokförd',
      'Overdue': 'Förfallen'
    };
    return statusNames[status] || status;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Scanned': { variant: 'info', icon: FileText },
      'Pending Approval': { variant: 'warning', icon: Clock },
      'Approved': { variant: 'success', icon: CheckCircle },
      'Pending Payment': { variant: 'warning', icon: Timer },
      'Paid': { variant: 'success', icon: CreditCard },
      'Verification Created': { variant: 'primary', icon: Receipt },
      'Overdue': { variant: 'danger', icon: AlertTriangle }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', icon: FileText };
    const IconComponent = config.icon;
    
    return (
      <Badge 
        bg={config.variant} 
        className="d-flex align-items-center gap-1"
        style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
      >
        <IconComponent size={14} />
        {getStatusDisplayName(status)}
      </Badge>
    );
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'Scanned': { icon: FileText, color: '#0ea5e9' },
      'Pending Approval': { icon: Clock, color: '#f59e0b' },
      'Approved': { icon: CheckCircle, color: '#10b981' },
      'Pending Payment': { icon: Timer, color: '#f59e0b' },
      'Paid': { icon: CreditCard, color: '#10b981' },
      'Verification Created': { icon: Receipt, color: '#44814E' },
      'Overdue': { icon: AlertTriangle, color: '#ef4444' }
    };
    
    return iconMap[status] || { icon: FileText, color: '#6b7280' };
  };

  const getNextAction = () => {
    switch (invoice.status) {
      case 'Scanned':
        return { text: 'Godkänn faktura', nextStatus: 'Approved', requireDate: false };
      case 'Approved':
        return { text: 'Betala faktura', nextStatus: 'Paid', requireDate: true };
      case 'Paid':
        return { text: "Bokför", nextStatus: 'Verification Created', requireDate: false };
      case 'Verification Created':
        return null; // No more actions
      case 'Overdue':
        return { text: 'Betala faktura', nextStatus: 'Paid', requireDate: true };
      default:
        return null;
    }
  };

  const nextAction = invoice ? getNextAction() : null;

  // Calculate days until/past due date
  const getDaysUntilDue = () => {
    if (!invoice || !invoice.due_date) return null;
    const today = dayjs();
    const dueDate = dayjs(invoice.due_date);
    const days = dueDate.diff(today, 'day');
    return days;
  };

  const daysUntilDue = invoice ? getDaysUntilDue() : null;

  const handleStatusUpdate = async (nextStatus) => {
    setMarkAsPaidLoading(true);
    setError(null);

    try {
      // API endpoint to update the invoice status
      const updateInvoiceStatusApiUrl = getApiUrl(
        `${companyId}/purchasing/supplier-invoices/${invoiceId}/status`
      );

      // For paid status, include the payment date
      const payload = { 
        status: nextStatus,
        ...(nextStatus === 'Paid' && { payment_date: paymentDate })
      };

      // Make the API call to update the status
      const response = await axios.put(
        updateInvoiceStatusApiUrl,
        payload,
        { withCredentials: true }
      );

      // Update the invoice state
      setInvoice((prevInvoice) => ({
        ...prevInvoice,
        status: nextStatus,
        payment_date: nextStatus === 'Paid' ? paymentDate : prevInvoice.payment_date,
        statusHistory: [...prevInvoice.statusHistory, { status: nextStatus, changed_at: new Date().toISOString() }]
      }));

      // Display success message
      setSuccessMessage(`Fakturan har uppdaterats till ${getStatusDisplayName(nextStatus).toLowerCase()}.`);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      setError('Kunde inte uppdatera fakturastatus. Vänligen försök igen senare.');
    } finally {
      setMarkAsPaidLoading(false);
    }
  };

  const handleViewVerification = async (verification) => {
    try {
      // Fetch the full verification details including transactions
      const verificationDetailsUrl = getApiUrl(
        `${companyId}/verification/${verification.verification_id}`
      );
      const response = await axios.get(verificationDetailsUrl, {
        withCredentials: true,
      });

      console.log('Raw verification API response:', response.data);

      // Transform the flat response into the structure the modal expects
      // The API returns an array where each row has both verification and transaction data
      const rows = response.data;

      if (!rows || rows.length === 0) {
        setError('Inga verifikationsdetaljer hittades.');
        return;
      }

      // Extract verification data from the first row
      const firstRow = rows[0];

      // Transform transactions into an array
      const transactions = rows.map(row => ({
        transaction_id: row.transaction_id,
        account_id: row.account_id,
        account_number: row.account_number,
        account_name: row.account_name,
        account_type: row.account_type,
        amount: row.amount,
        transaction_type: row.transaction_type,
        description: row.transaction_description
      }));

      // Calculate debit and credit sums
      let debitSum = 0;
      let creditSum = 0;
      transactions.forEach(transaction => {
        if (transaction.transaction_type === 'debit') {
          debitSum += parseFloat(transaction.amount);
        } else if (transaction.transaction_type === 'credit') {
          creditSum += parseFloat(transaction.amount);
        }
      });

      const verificationData = {
        verification_id: firstRow.verification_id,
        date: firstRow.date,
        verification_description: firstRow.verification_description,
        series: firstRow.series,
        number: firstRow.number,
        created_at: firstRow.created_at,
        reference: firstRow.reference,
        notes: firstRow.notes,
        transactions,
        debitSum,
        creditSum
      };

      console.log('Transformed verification data:', verificationData);
      setSelectedVerification(verificationData);
      setShowVerificationDetails(true);
    } catch (error) {
      console.error('Error fetching verification details:', error);
      setError('Kunde inte hämta verifikationsdetaljer.');
    }
  };

  const handleVerificationUpdated = () => {
    // Refresh verifications list when a verification is updated
    // You can fetch the updated list here if needed
  };

  const handleActionClick = () => {
    if (nextAction.nextStatus === 'Verification Created') {
      // Open the modal for verification creation
      setShowVerificationModal(true);
    } else {
      // Show the confirmation modal
      setShowConfirmModal(true);
    }
  };

  const handleConfirmVerification = async (verificationData) => {
    setMarkAsPaidLoading(true);
    setError(null);
    try {
      // Use the new dedicated endpoint for supplier invoice verifications
      // This endpoint automatically handles:
      // - Fetching invoice details and attachments
      // - Linking attachments to verification
      // - Updating invoice status
      const createVerificationApiUrl = getApiUrl(
        `${companyId}/purchasing/supplier-invoices/${invoiceId}/verifications`
      );

      const date = new Date().toISOString().split('T')[0];
      const description = `Betalning av faktura ${invoice.invoice_number}`;

      const response = await axios.post(
        createVerificationApiUrl,
        {
          date,
          description,
          transactions: verificationData.transactions
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        // Update the invoice status and history
        const nextStatus = 'Verification Created';
        setInvoice((prevInvoice) => ({
          ...prevInvoice,
          status: nextStatus,
          statusHistory: [
            ...prevInvoice.statusHistory,
            { status: nextStatus, changed_at: new Date().toISOString() },
          ],
        }));

        // Close the modal
        setShowVerificationModal(false);

        // Display success message
        setSuccessMessage('Verifikation skapad.');
        
        // Refresh verifications
        const getAssociatedVerificationsApiUrl = getApiUrl(
          `${companyId}/purchasing/supplier-invoices/${invoiceId}/verifications`
        );
        const verificationResponse = await axios.get(getAssociatedVerificationsApiUrl, {
          withCredentials: true,
        });
        setVerifications(verificationResponse.data);
      } else {
        throw new Error('Failed to create verification');
      }
    } catch (error) {
      console.error('Error creating verification:', error);
      setError('Kunde inte skapa verifikation. Vänligen försök igen senare.');
    } finally {
      setMarkAsPaidLoading(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <PageTitle activeMenu={`Faktura ${invoice?.invoice_number}`} motherMenu="Leverantörsfakturor" />
      
      <style jsx>{`
        .modern-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .modern-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        }

        .detail-item {
          background: #f8fafc;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.75rem;
          border-left: 3px solid #44814E;
        }

        .detail-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e293b;
        }

        .timeline-modern {
          position: relative;
          padding-left: 2rem;
        }

        .timeline-modern::before {
          content: '';
          position: absolute;
          left: 1rem;
          top: 0;
          bottom: 0;
          width: 2px;
          background: linear-gradient(to bottom, #44814E, #e2e8f0);
        }

        .timeline-item {
          position: relative;
          margin-bottom: 1.5rem;
          background: white;
          border-radius: 8px;
          padding: 1rem;
          border: 1px solid #e2e8f0;
        }

        .timeline-icon {
          position: absolute;
          left: -2.5rem;
          top: 1rem;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: white;
          border: 2px solid #44814E;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .attachment-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
          background: white;
          transition: all 0.2s ease;
        }

        .attachment-card:hover {
          border-color: #44814E;
          box-shadow: 0 2px 8px rgba(68, 129, 78, 0.1);
        }

        .action-button {
          background: #44814E;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .action-button:hover {
          background: #3a6f42;
          transform: translateY(-1px);
        }

        .back-button {
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .back-button:hover {
          background: #e2e8f0;
          color: #334155;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.25rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          border: none;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--accent-color, #44814E), var(--accent-color-light, #5a9f68));
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.125rem;
          position: relative;
        }

        .stat-icon::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 12px;
          background: inherit;
          opacity: 0.1;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.375rem;
          color: #1f2937;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }

        .stat-subtitle {
          color: #9ca3af;
          font-size: 0.6875rem;
          font-weight: 400;
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }

          .stat-value {
            font-size: 1.5rem;
          }
        }

        /* Modern Horizontal Timeline */
        .horizontal-timeline {
          position: relative;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 3rem 2rem 1.5rem;
          overflow-x: auto;
        }

        .horizontal-timeline::before {
          content: '';
          position: absolute;
          top: calc(3rem + 28px);
          left: 5%;
          right: 5%;
          height: 3px;
          background: linear-gradient(to right, #44814E, #e2e8f0);
          z-index: 0;
        }

        .timeline-step {
          position: relative;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 140px;
          z-index: 1;
        }

        .timeline-step-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          border: 3px solid #e2e8f0;
          margin-bottom: 1.25rem;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          position: relative;
          z-index: 2;
        }

        .timeline-step.completed .timeline-step-icon {
          border-color: #44814E;
          background: #f0fdf4;
          box-shadow: 0 4px 12px rgba(68, 129, 78, 0.2);
        }

        .timeline-step.active .timeline-step-icon {
          border-color: #44814E;
          background: #44814E;
          box-shadow: 0 4px 16px rgba(68, 129, 78, 0.3);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 4px 16px rgba(68, 129, 78, 0.3);
          }
          50% {
            box-shadow: 0 4px 24px rgba(68, 129, 78, 0.5);
          }
        }

        .timeline-step-content {
          text-align: center;
        }

        .timeline-step-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 0.25rem;
        }

        .timeline-step.completed .timeline-step-title,
        .timeline-step.active .timeline-step-title {
          color: #44814E;
        }

        .timeline-step-date {
          font-size: 0.75rem;
          color: #94a3b8;
        }

        .timeline-step.completed .timeline-step-date,
        .timeline-step.active .timeline-step-date {
          color: #64748b;
          font-weight: 500;
        }

        @media (max-width: 1024px) {
          .horizontal-timeline {
            overflow-x: scroll;
            padding-bottom: 1.5rem;
          }

          .timeline-step {
            min-width: 120px;
          }

          .timeline-step-icon {
            width: 48px;
            height: 48px;
          }
        }
      `}</style>
      
      {/* Display error messages */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="modern-card mb-4">
          <AlertCircle size={20} className="me-2" />
          {error}
        </Alert>
      )}
      
      {/* Display success messages */}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible className="modern-card mb-4">
          <CheckCircle size={20} className="me-2" />
          {successMessage}
        </Alert>
      )}

      {/* Check if invoice exists */}
      {!invoice ? (
        <Alert variant="warning" className="modern-card">
          <AlertTriangle size={20} className="me-2" />
          Fakturan kunde inte hittas.
        </Alert>
      ) : (
        <>
          {/* Back Button - Top Left */}
          <div className="mb-3">
            <Button
              variant="link"
              className="p-0 text-decoration-none d-flex align-items-center gap-2"
              onClick={() => history.goBack()}
              style={{ color: '#64748b', fontSize: '0.875rem' }}
            >
              <ArrowLeft size={16} />
              Tillbaka till listan
            </Button>
          </div>

          {/* Header Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h2 className="mb-2">Faktura {invoice.invoice_number}</h2>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Building size={16} />
                    <span style={{ fontWeight: '500' }}>{invoice.supplier_name}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2" style={{
                    padding: '0.25rem 0.75rem',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    fontSize: '0.8125rem'
                  }}>
                    <CalendarDays size={14} className="text-muted" />
                    <span className="text-muted">Fakturadatum:</span>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>
                      {dayjs(invoice.invoice_date).format('DD MMM YYYY')}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2" style={{
                    padding: '0.25rem 0.75rem',
                    background: daysUntilDue < 0 ? '#fef2f2' : daysUntilDue <= 7 ? '#fffbeb' : '#f0fdf4',
                    borderRadius: '6px',
                    fontSize: '0.8125rem'
                  }}>
                    <Timer size={14} style={{
                      color: daysUntilDue < 0 ? '#ef4444' : daysUntilDue <= 7 ? '#f59e0b' : '#10b981'
                    }} />
                    <span className="text-muted">Förfaller:</span>
                    <span style={{
                      fontWeight: '600',
                      color: daysUntilDue < 0 ? '#ef4444' : daysUntilDue <= 7 ? '#f59e0b' : '#10b981'
                    }}>
                      {dayjs(invoice.due_date).format('DD MMM YYYY')}
                    </span>
                    {daysUntilDue < 0 && (
                      <Badge bg="danger" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                        {Math.abs(daysUntilDue)}d försenad
                      </Badge>
                    )}
                    {daysUntilDue >= 0 && daysUntilDue <= 7 && (
                      <Badge bg="warning" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                        {daysUntilDue}d kvar
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                {getStatusBadge(invoice.status)}
                {nextAction && (
                  <Button
                    className="action-button d-flex align-items-center gap-2"
                    onClick={handleActionClick}
                    disabled={markAsPaidLoading}
                  >
                    {markAsPaidLoading ? (
                      <>
                        <Spinner size="sm" />
                        Uppdaterar...
                      </>
                    ) : (
                      <>
                        {nextAction.nextStatus === 'Approved' && <CheckCircle size={16} />}
                        {nextAction.nextStatus === 'Paid' && <CreditCard size={16} />}
                        {nextAction.nextStatus === 'Verification Created' && <Receipt size={16} />}
                        {nextAction.text}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="stats-grid">
            {/* Amount Card */}
            <div className="stat-card" style={{ '--accent-color': '#0ea5e9', '--accent-color-light': '#38bdf8' }}>
              <div className="stat-icon" style={{ background: '#f0f9ff', color: '#0ea5e9' }}>
                <Banknote size={24} />
              </div>
              <div className="stat-value" style={{ color: '#0ea5e9' }}>
                {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(parseFloat(invoice.amount) || 0)}
              </div>
              <div className="stat-label">Fakturabelopp</div>
              <div className="stat-subtitle">
                {invoice.invoice_number}
              </div>
            </div>

            {/* Status Card */}
            <div className="stat-card" style={{
              '--accent-color': invoice.status === 'Paid' || invoice.status === 'Verification Created' ? '#10b981' :
                               invoice.status === 'Overdue' ? '#ef4444' : '#f59e0b',
              '--accent-color-light': invoice.status === 'Paid' || invoice.status === 'Verification Created' ? '#34d399' :
                                     invoice.status === 'Overdue' ? '#f87171' : '#fbbf24'
            }}>
              <div className="stat-icon" style={{
                background: invoice.status === 'Paid' || invoice.status === 'Verification Created' ? '#ecfdf5' :
                           invoice.status === 'Overdue' ? '#fef2f2' : '#fffbeb',
                color: invoice.status === 'Paid' || invoice.status === 'Verification Created' ? '#10b981' :
                       invoice.status === 'Overdue' ? '#ef4444' : '#f59e0b'
              }}>
                {(() => {
                  const { icon: IconComponent } = getStatusIcon(invoice.status);
                  return <IconComponent size={24} />;
                })()}
              </div>
              <div className="stat-value" style={{
                color: invoice.status === 'Paid' || invoice.status === 'Verification Created' ? '#10b981' :
                       invoice.status === 'Overdue' ? '#ef4444' : '#f59e0b',
                fontSize: '1.25rem'
              }}>
                {getStatusDisplayName(invoice.status)}
              </div>
              <div className="stat-label">Status</div>
              <div className="stat-subtitle">
                Uppdaterad {dayjs(invoice.statusHistory?.[invoice.statusHistory.length - 1]?.changed_at).format('DD MMM')}
              </div>
            </div>

            {/* Due Date Card (if no payments) OR Payment Date Card (if has payments) */}
            {invoice.payments && invoice.payments.length > 0 ? (
              // Show Payment Date card when payments exist
              <div className="stat-card" style={{ '--accent-color': '#10b981', '--accent-color-light': '#34d399' }}>
                <div className="stat-icon" style={{ background: '#ecfdf5', color: '#10b981' }}>
                  <CheckCircle2 size={24} />
                </div>
                <div className="stat-value" style={{ color: '#10b981', fontSize: '1.25rem' }}>
                  {dayjs(invoice.payment_date).format('DD MMM YYYY')}
                </div>
                <div className="stat-label">Betaldatum</div>
                <div className="stat-subtitle">
                  Betalad för {dayjs().diff(dayjs(invoice.payment_date), 'day')} dagar sedan
                </div>
              </div>
            ) : (
              // Show Due Date card when no payments
              <div className="stat-card" style={{
                '--accent-color': daysUntilDue < 0 ? '#ef4444' : daysUntilDue <= 7 ? '#f59e0b' : '#10b981',
                '--accent-color-light': daysUntilDue < 0 ? '#f87171' : daysUntilDue <= 7 ? '#fbbf24' : '#34d399'
              }}>
                <div className="stat-icon" style={{
                  background: daysUntilDue < 0 ? '#fef2f2' : daysUntilDue <= 7 ? '#fffbeb' : '#ecfdf5',
                  color: daysUntilDue < 0 ? '#ef4444' : daysUntilDue <= 7 ? '#f59e0b' : '#10b981'
                }}>
                  {daysUntilDue < 0 ? <AlertTriangle size={24} /> : daysUntilDue <= 7 ? <Clock size={24} /> : <Calendar size={24} />}
                </div>
                <div className="stat-value" style={{
                  color: daysUntilDue < 0 ? '#ef4444' : daysUntilDue <= 7 ? '#f59e0b' : '#10b981'
                }}>
                  {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} dagar` : daysUntilDue === 0 ? 'Idag' : `${daysUntilDue} dagar`}
                </div>
                <div className="stat-label">
                  {daysUntilDue < 0 ? 'Försenad' : 'Förfaller om'}
                </div>
                <div className="stat-subtitle">
                  {dayjs(invoice.due_date).format('DD MMM YYYY')}
                </div>
              </div>
            )}

            {/* Supplier Card */}
            <div className="stat-card" style={{ '--accent-color': '#8b5cf6', '--accent-color-light': '#a78bfa' }}>
              <div className="stat-icon" style={{ background: '#faf5ff', color: '#8b5cf6' }}>
                <Building size={24} />
              </div>
              <div className="stat-value" style={{ color: '#8b5cf6', fontSize: '1.125rem' }}>
                {invoice.supplier_name}
              </div>
              <div className="stat-label">Leverantör</div>
              <div className="stat-subtitle">
                {invoice.supplier_orgnumber || 'Inget org.nr'}
              </div>
            </div>

            {/* Verification Status Card (if applicable) */}
            {verifications && verifications.length > 0 && (
              <div className="stat-card" style={{ '--accent-color': '#44814E', '--accent-color-light': '#5a9f68' }}>
                <div className="stat-icon" style={{ background: '#f0fdf4', color: '#44814E' }}>
                  <Receipt size={24} />
                </div>
                <div className="stat-value" style={{ color: '#44814E', fontSize: '1.25rem' }}>
                  {verifications.length}
                </div>
                <div className="stat-label">Verifikationer</div>
                <div className="stat-subtitle">
                  {verifications.map((v, i) => (
                    <span
                      key={v.verification_id}
                      onClick={() => handleViewVerification(v)}
                      style={{
                        color: '#44814E',
                        textDecoration: 'none',
                        marginRight: '0.25rem',
                        cursor: 'pointer'
                      }}
                    >
                      {v.series}{v.number}{i < verifications.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Modern Horizontal Status Timeline */}
          <Card className="modern-card mb-4">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 d-flex align-items-center gap-2">
                  <Clock size={20} className="text-primary" />
                  Behandlingshistorik
                </h5>
                {invoice.payment_date && (
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.8125rem', color: '#10b981' }}>
                    <CheckCircle2 size={16} />
                    Betald {dayjs(invoice.payment_date).format('DD MMM YYYY')}
                  </div>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="horizontal-timeline">
                {(() => {
                  // Define all possible workflow steps
                  const allSteps = [
                    { status: 'Scanned', label: 'Skannad', icon: FileText },
                    { status: 'Approved', label: 'Godkänd', icon: CheckCircle },
                    { status: 'Paid', label: 'Betald', icon: CreditCard },
                    { status: 'Verification Created', label: 'Bokförd', icon: Receipt }
                  ];

                  // Create a map of completed statuses with their timestamps
                  const statusMap = {};
                  invoice.statusHistory?.forEach(event => {
                    statusMap[event.status] = event.changed_at;
                  });

                  // Get current status index
                  const currentStatusIndex = allSteps.findIndex(step => step.status === invoice.status);

                  return allSteps.map((step, index) => {
                    const isCompleted = statusMap[step.status];
                    const isActive = invoice.status === step.status;
                    const IconComponent = step.icon;
                    const { color } = getStatusIcon(step.status);

                    return (
                      <div
                        key={step.status}
                        className={`timeline-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                      >
                        <div className="timeline-step-icon">
                          <IconComponent
                            size={24}
                            style={{
                              color: isActive ? 'white' : isCompleted ? color : '#cbd5e1'
                            }}
                          />
                        </div>
                        <div className="timeline-step-content">
                          <div className="timeline-step-title">{step.label}</div>
                          {isCompleted && (
                            <div className="timeline-step-date">
                              {dayjs(statusMap[step.status]).format('DD MMM YYYY')}
                              <br />
                              {dayjs(statusMap[step.status]).format('HH:mm')}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Verification Links */}
              {verifications && verifications.length > 0 && (
                <div className="mt-3 pt-3 border-top text-center">
                  <div className="mb-2" style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Kopplade Verifikationer
                  </div>
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    {verifications.map((verification) => (
                      <Badge
                        key={verification.verification_id}
                        bg="light"
                        text="dark"
                        className="d-inline-flex align-items-center gap-2 py-2 px-3"
                        style={{
                          fontSize: '0.8125rem',
                          border: '1px solid #e2e8f0',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleViewVerification(verification)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#44814E';
                          e.currentTarget.style.background = '#f0fdf4';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.background = '#f8f9fa';
                        }}
                      >
                        <Receipt size={14} style={{ color: '#44814E' }} />
                        <span style={{ color: '#44814E', fontWeight: '600' }}>
                          {verification.series}{verification.number}
                        </span>
                        <ExternalLink size={12} style={{ color: '#64748b' }} />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Row>
            {/* Main Content - Invoice Document Viewer */}
            <Col xs={12}>
              {/* Attachments Section - Primary Focus */}
              {invoice.attachments && invoice.attachments.length > 0 && invoice.attachments[0].attachment_id ? (
                <Card className="modern-card">
                  <Card.Header className="bg-white border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 d-flex align-items-center gap-2">
                        <FileImage size={20} className="text-primary" />
                        Fakturadokument
                      </h5>
                      {invoice.attachments.length > 1 && (
                        <Badge bg="info" className="d-flex align-items-center gap-1">
                          <File size={12} />
                          {invoice.attachments.length} bilagor
                        </Badge>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {invoice.attachments.map((attachment, index) => (
                      <div key={attachment.attachment_id} className="mb-4">
                        {/* File Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                          <div className="d-flex align-items-center gap-2">
                            {attachment.file_type === 'application/pdf' ? (
                              <File size={18} className="text-danger" />
                            ) : attachment.file_type.startsWith('image/') ? (
                              <FileImage size={18} className="text-info" />
                            ) : (
                              <File size={18} className="text-secondary" />
                            )}
                            <span className="fw-medium" style={{ fontSize: '0.875rem' }}>
                              {attachment.file_name}
                            </span>
                          </div>
                          {attachmentUrls[attachment.attachment_id] && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              href={attachmentUrls[attachment.attachment_id]}
                              download={attachment.file_name}
                              className="d-flex align-items-center gap-1"
                            >
                              <Download size={14} />
                              Ladda ner
                            </Button>
                          )}
                        </div>

                        {/* File Viewer */}
                        {attachment.file_type === 'application/pdf' && attachmentUrls[attachment.attachment_id] ? (
                          <div style={{ height: '800px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                            <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                              <Viewer fileUrl={attachmentUrls[attachment.attachment_id]} />
                            </Worker>
                          </div>
                        ) : attachment.file_type.startsWith('image/') && attachmentUrls[attachment.attachment_id] ? (
                          <img
                            src={attachmentUrls[attachment.attachment_id]}
                            alt={attachment.file_name}
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                          />
                        ) : !attachmentUrls[attachment.attachment_id] ? (
                          <div className="text-center py-5">
                            <Spinner animation="border" className="mb-2" />
                            <div className="text-muted">Laddar fil...</div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              ) : (
                <Card className="modern-card">
                  <Card.Body className="text-center py-5">
                    <FileImage size={48} className="text-muted mb-3" />
                    <p className="text-muted">Ingen bifogad faktura finns tillgänglig</p>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>

          {/* Confirmation Modal */}
          {nextAction && (
            <ConfirmationModal
              show={showConfirmModal}
              onHide={() => setShowConfirmModal(false)}
              title="Bekräfta åtgärd"
              body={`Är du säker på att du vill ${nextAction.text.toLowerCase()}?`}
              confirmText={`Ja, ${nextAction.text.toLowerCase()}`}
              onConfirm={() => {
                setShowConfirmModal(false);
                handleStatusUpdate(nextAction.nextStatus);
              }}
              isLoading={markAsPaidLoading}
              requireDate={nextAction.requireDate}
              dateValue={paymentDate}
              onDateChange={setPaymentDate}
              dateLabel="Betaldatum"
              invoiceDate={invoice.invoice_date}
              invoiceDueDate={invoice.due_date}
            />
          )}

          {/* Verification Modal */}
          <VerificationModal
            show={showVerificationModal}
            onHide={() => setShowVerificationModal(false)}
            companyId={companyId}
            invoice={invoice}
            invoiceId={invoiceId}
            onConfirm={handleConfirmVerification}
            isLoading={markAsPaidLoading}
            error={error}
            setError={setError}
          />

          {/* Verification Details Modal */}
          {selectedVerification && (
            <VerificationDetailsModal
              showVerificationDetails={showVerificationDetails}
              setShowVerificationDetails={setShowVerificationDetails}
              selectedRow={selectedVerification}
              onVerificationUpdated={handleVerificationUpdated}
              onUploadAttachment={(verificationId) => {
                // Handle attachment upload if needed
                console.log('Upload attachment for verification:', verificationId);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default ViewSupplierInvoice;